import { cn } from "@qn/ui";
import { WifiOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import type { User } from "../lib/api";
import { useFocus } from "../lib/focus";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { CallHost } from "./CallHost";
import { CommandPalette } from "./CommandPalette";
import { ErrorBoundary } from "./ErrorBoundary";
import { ShortcutsDialog } from "./ShortcutsDialog";
import { Sidebar } from "./Sidebar";
import { UpdateBanner } from "./UpdateBanner";

function useOnline() {
  const [on, setOn] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    addEventListener("online", up);
    addEventListener("offline", down);
    return () => {
      removeEventListener("online", up);
      removeEventListener("offline", down);
    };
  }, []);
  return on;
}

const EDGE = 28; // px from the start edge where an opening swipe may begin
const OPEN_AT = 72; // px of drag that opens/closes the drawer

/**
 * Edge swipe for the mobile drawer: drag from the sidebar edge to open it (it follows the finger),
 * drag it back to close. Direction-aware (RTL opens from the right edge).
 */
function useDrawerSwipe(
  open: boolean,
  setOpen: (v: boolean) => void,
  el: React.RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let startX = 0;
    let startY = 0;
    let mode: "open" | "close" | null = null;
    let locked = false;
    const rtl = () => document.documentElement.dir === "rtl";
    const width = () => el.current?.offsetWidth ?? 296;
    // Distance dragged toward the inside of the screen (positive = opening direction).
    const delta = (x: number) => (rtl() ? startX - x : x - startX);
    const reset = () => {
      if (el.current) {
        el.current.style.transform = "";
        el.current.style.transition = "";
      }
    };
    const onStart = (e: TouchEvent) => {
      if (window.innerWidth >= 1024 || e.touches.length !== 1) return;
      const t = e.touches[0] as Touch;
      startX = t.clientX;
      startY = t.clientY;
      const fromEdge = rtl() ? window.innerWidth - t.clientX : t.clientX;
      mode = open ? "close" : fromEdge <= EDGE ? "open" : null;
      locked = false;
    };
    const onMove = (e: TouchEvent) => {
      if (!mode || !el.current) return;
      const t = e.touches[0] as Touch;
      const dx = delta(t.clientX);
      if (!locked) {
        if (Math.abs(t.clientY - startY) > 12 && Math.abs(t.clientY - startY) > Math.abs(dx)) {
          mode = null; // vertical scroll, not a drawer drag
          return;
        }
        if (Math.abs(dx) < 8) return;
        locked = true;
        el.current.style.transition = "none";
      }
      const w = width();
      const shown =
        mode === "open" ? Math.min(Math.max(dx, 0), w) : Math.min(Math.max(w + dx, 0), w);
      el.current.style.transform = `translateX(${rtl() ? w - shown : shown - w}px)`;
    };
    const onEnd = (e: TouchEvent) => {
      if (!mode) return;
      const t = e.changedTouches[0] as Touch;
      const dx = delta(t.clientX);
      const wasLocked = locked;
      const m = mode;
      mode = null;
      reset();
      if (!wasLocked) return;
      if (m === "open" && dx > OPEN_AT) setOpen(true);
      if (m === "close" && dx < -OPEN_AT) setOpen(false);
    };
    addEventListener("touchstart", onStart, { passive: true });
    addEventListener("touchmove", onMove, { passive: true });
    addEventListener("touchend", onEnd, { passive: true });
    addEventListener("touchcancel", reset, { passive: true });
    return () => {
      removeEventListener("touchstart", onStart);
      removeEventListener("touchmove", onMove);
      removeEventListener("touchend", onEnd);
      removeEventListener("touchcancel", reset);
    };
  }, [open, setOpen, el]);
}

export function Shell({ user }: { user: User }) {
  const t = useT();
  const online = useOnline();
  const open = usePrefs((s) => s.sidebarOpen);
  const set = usePrefs((s) => s.set);
  const drawer = useRef<HTMLElement>(null);
  const focus = useFocus((s) => s.on);
  useEffect(() => {
    document.documentElement.dataset.focus = focus ? "on" : "off";
    return () => {
      document.documentElement.dataset.focus = "off";
    };
  }, [focus]);
  useDrawerSwipe(open, (v) => set({ sidebarOpen: v }), drawer);
  const closeOnMobile = () => {
    if (window.innerWidth < 1024) set({ sidebarOpen: false });
  };
  return (
    <div className="flex h-dvh overflow-hidden">
      {open && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-[2px] lg:hidden"
          onClick={() => set({ sidebarOpen: false })}
        />
      )}
      <button
        type="button"
        onClick={() => document.getElementById("main")?.focus()}
        className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-[var(--accent)] focus:px-3 focus:py-2 focus:text-sm focus:text-[var(--accent-fg)]"
      >
        {t("a11y.skip")}
      </button>
      <aside
        data-shell="sidebar"
        aria-label={t("sidebar.title")}
        ref={drawer}
        className={cn(
          "glass fixed inset-y-0 start-0 z-40 w-[18.5rem] border-e border-[var(--line)] transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:backdrop-blur-none lg:[background:color-mix(in_oklab,var(--surface)_55%,transparent)]",
          open ? "translate-x-0" : "ltr:-translate-x-full rtl:translate-x-full lg:hidden",
        )}
        aria-hidden={!open}
        inert={!open}
      >
        <Sidebar user={user} onNavigate={closeOnMobile} />
      </aside>
      <CommandPalette />
      <CallHost />
      <ShortcutsDialog />
      <main id="main" tabIndex={-1} className="flex min-w-0 flex-1 flex-col outline-none">
        <UpdateBanner />
        {!online && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 bg-[color-mix(in_oklab,var(--warn)_16%,var(--surface))] px-3 py-1.5 text-xs text-[var(--warn)]"
          >
            <WifiOff className="size-3.5" /> {t("offline.banner")}
          </div>
        )}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
        {focus && (
          <button
            type="button"
            onClick={() => useFocus.getState().off()}
            className="glass fixed bottom-4 end-4 z-30 rounded-full border border-[var(--line)] px-3 py-1.5 text-xs text-[var(--fg-muted)] hover:text-[var(--fg)]"
          >
            {t("focus.exit")}
          </button>
        )}
      </main>
    </div>
  );
}
